import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { Alert, Tree } from '@fluentui/react-components/unstable'
import { InvoiceProps } from './InvoiceForm'
import { useFormikContext } from 'formik'
import {
    ConsolidationType,
    InvoiceMutationDto,
    LineItemDto,
    LineItemGroupDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
    LineItemType,
    MetaDataEntities,
} from '../../../../config/finance/invoiceConfig'
import { makeStyles, Toolbar, ToolbarButton, ToolbarGroup, Tooltip } from '@fluentui/react-components'
import { AddRegular, ReOrderDotsVerticalRegular } from '@fluentui/react-icons'
import { css, Stack, StackItem } from '@fluentui/react'
import { baseListStyles } from '../../../List/BaseList'
import LineItemGroup from './LineItemGroup/LineItemGroup'
import LineItemGroupDialog from './LineItemGroup/Dialog/LineItemGroupDialog'
import { PricePrecedence } from '../../../../config/finance/financeConfig'
import * as UUID from 'uuid'
import { LineItemEntity } from '../../../../hooks/finance/invoice/useLineItemEntities'
import { useTranslation } from 'react-i18next'

type Props = FieldArrayRenderProps & InvoiceProps

const useStyles = makeStyles({
    toolbar: {
        justifyContent: 'end',
    },
    treeItem: {
        marginLeft: '-16px',
        marginRight: '-16px',
        '&.is-selected': {
            marginLeft: '-16px',
            marginRight: '-16px',
            paddingLeft: '7px',
            paddingRight: '7px',
        },
    },
})

const lineItemMetaKeys = new Map<LineItemType, string>([
    [LineItemType.Disbursement, 'disbursements'],
    [LineItemType.FixedPrice, 'fixedPrices'],
    [LineItemType.Invoice, 'invoices'],
    [LineItemType.Surcharge, 'surcharges'],
    [LineItemType.Travel, 'travels'],
    [LineItemType.Time, 'times'],
])

export const createLineItemMap = (ids: string[], data: MetaDataEntities, include: boolean): Map<LineItemType, LineItemEntity[]> => {
    return new Map(
        (Object.values(LineItemType) as LineItemType[]).map(lineItemType => [
            lineItemType,
            data?.files
                ?.flatMap(file => file[lineItemMetaKeys.get(lineItemType)])
                ?.filter((entity: LineItemEntity) => ids.includes(entity.id) === include) || [],
        ]),
    )
}

export const createValuesLineItemMap = (ids: string[], data: LineItemGroupDto[], include: boolean): Map<LineItemType, LineItemDto[]> => {
    return new Map(
        (Object.values(LineItemType) as LineItemType[]).map(lineItemType => [
            lineItemType,
            data?.flatMap(group => group.lineItems)?.filter((entity: LineItemMutationDto) => ids.includes(entity.id) === include) || [],
        ]),
    )
}

export default function LineItemGroups(props: Props): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const {
        values: { lineItemGroups },
        setFieldValue,
    } = useFormikContext<InvoiceMutationDto>()
    const firstFile = useMemo(() => props.meta.data.files?.[0]?.file, [props.meta.data.files])

    const [add, setAdd] = useState(false)
    const [currentPosition, setCurrentPosition] = useState<number>()
    const [nextPosition, setNextPosition] = useState<number>()
    const elements = useRef<HTMLElement[]>([])
    const [currentLineItem, setCurrentLineItem] = useState<LineItemMutationDto>()
    const addCallback = () => {
        props.push({
            id: UUID.v4(),
            fileId: firstFile?.id,
            isNew: true,
            description: '',
            price: '0.00',
            precedence: PricePrecedence.Inherited,
            showStatement: false,
            consolidation: ConsolidationType.NONE,
            vatType: props.defaultVatType,
            lineItems: [],
            fixedPriceId: undefined,
            accounting: undefined,
        })
        setAdd(true)
    }

    useEffect(() => {
        // Make sure that the refs are always kept updated.
        // Courtesy of https://stackoverflow.com/a/56063129
        elements.current = elements.current.slice(0, lineItemGroups.length)
    }, [lineItemGroups])

    useEffect(() => {
        if (!currentLineItem && nextPosition !== undefined && nextPosition !== currentPosition) {
            props.move(currentPosition, nextPosition)
            setCurrentPosition(nextPosition)
            setNextPosition(undefined)
        }
    }, [nextPosition])

    if (!lineItemGroups?.length) {
        return (
            <Alert
                intent="info"
                action={
                    props.isEditable && {
                        icon: <AddRegular />,
                        onClick: addCallback,
                        children: translate('action.add'),
                    }
                }
            >
                {translate('invoice.edit.form.invoiceline.empty')}
            </Alert>
        )
    }

    return (
        <Tree
            size="medium"
            aria-label={translate('invoice.edit.form.invoiceline.label')}
            appearance="transparent"
            className={css(baseListStyles.listContainer)}
            onDragOver={event => {
                // Allow the whole tree to be dragged over.
                event.preventDefault()

                // Only allow for groups to be dragged if we're not currently dragging a line item. Without a
                // position there is no group being dragged at all: the pointer is merely passing over the tree.
                if (currentLineItem || !props.isEditable || currentPosition === undefined) {
                    return
                }

                // The refs are rebuilt whenever the groups change, so the dragged element can be gone by now.
                const currentElement = elements.current[currentPosition]

                if (!currentElement) {
                    return
                }

                const nextElement = elements.current.find((element: HTMLElement, position: number) =>
                    // Only initiate the move when we passed at least half of the next item.
                    position <= currentPosition
                        ? event.clientY < element.offsetTop + (element.offsetHeight + currentElement.offsetHeight) / 2
                        : event.clientY > element.offsetTop + (element.offsetHeight - currentElement.offsetHeight) / 2,
                )

                if (nextElement) {
                    setNextPosition(elements.current.findIndex(element => element === nextElement))
                }
            }}
            onDragEnter={event => {
                // Fix incidental collapses into non dragging state.
                event.preventDefault()
            }}
            onDragEnd={() => {
                // Line items are only relocated after dragging finished.
                if (currentLineItem && nextPosition !== undefined) {
                    const sourceGroupIndex = lineItemGroups.findIndex(group => group.lineItems.includes(currentLineItem))

                    if (nextPosition !== sourceGroupIndex) {
                        const targetLineItems = [...lineItemGroups[nextPosition].lineItems, { ...currentLineItem }]
                        const sourceLineItems = [...lineItemGroups[sourceGroupIndex].lineItems].filter(item => item !== currentLineItem)

                        setFieldValue(`lineItemGroups[${sourceGroupIndex}].lineItems`, sourceLineItems)
                        setFieldValue(`lineItemGroups[${nextPosition}].lineItems`, targetLineItems)
                    }
                }

                setCurrentLineItem(undefined)
                setCurrentPosition(undefined)
                setNextPosition(undefined)
            }}
        >
            {props.isEditable && (
                <Toolbar size="small" className={styles.toolbar}>
                    <ToolbarGroup>
                        <Tooltip content={translate('invoice.edit.form.invoicegroup.add.tooltip')} relationship="description" withArrow>
                            <ToolbarButton icon={<AddRegular />} onClick={addCallback}>
                                {translate('action.add')}
                            </ToolbarButton>
                        </Tooltip>
                        {add && (
                            <LineItemGroupDialog
                                {...props}
                                key={`dialog-group-new-${lineItemGroups.at(-1).id}`}
                                open={add}
                                setOpen={setAdd}
                                isNew
                                index={lineItemGroups.length - 1}
                                lineItemGroup={lineItemGroups.at(-1)}
                            />
                        )}
                    </ToolbarGroup>
                </Toolbar>
            )}
            {lineItemGroups.map((group: LineItemGroupMutationDto, index: number) => (
                <div key={group.id} ref={element => (elements.current[index] = element)}>
                    <Stack
                        horizontal
                        verticalAlign="center"
                        draggable={props.isEditable}
                        onDragStart={() => props.isEditable && setCurrentPosition(index)}
                        onDragOver={() => props.isEditable && currentLineItem && setNextPosition(index)}
                        className={css(
                            baseListStyles.itemCell,
                            currentPosition !== undefined && 'is-dragging',
                            !currentLineItem && currentPosition === index && 'is-dragged',
                            currentLineItem && nextPosition === index && 'is-selected',
                            styles.treeItem,
                        )}
                    >
                        <StackItem grow>
                            <LineItemGroup
                                {...props}
                                lineItemGroup={group}
                                index={index}
                                prefix={`lineItemGroups[${index}]`}
                                isDragging={currentPosition !== undefined}
                                setCurrentLineItem={setCurrentLineItem}
                            />
                        </StackItem>
                        {props.isEditable && <ReOrderDotsVerticalRegular style={{ cursor: 'move' }} />}
                    </Stack>
                </div>
            ))}
        </Tree>
    )
}
